import { useState, useEffect, useRef } from 'react';
import { 
  collection, 
  addDoc, 
  doc, 
  updateDoc, 
  deleteDoc,
  onSnapshot,
  FirestoreError
} from 'firebase/firestore';
import { WorkstreamType } from '../types';
import { migrateDefaultWorkstreamTypes } from '../utils/migrateWorkstreamTypes';
import { firestore } from '../../../firebase/config';

export const useWorkstreamTypes = (workspaceId: string) => {
  const [workstreamTypes, setWorkstreamTypes] = useState<WorkstreamType[]>([]);
  const [status, setStatus] = useState<'loading' | 'error' | 'success'>('loading');
  const migrationCompleted = useRef(false);
  
  useEffect(() => {
    const checkAndMigrateTypes = async () => {
      if (migrationCompleted.current) return;
      try {
        await migrateDefaultWorkstreamTypes(firestore, workspaceId);
      } catch (error) {
        console.error('Error during workstream types migration:', error);
      }
      migrationCompleted.current = true;
    };

    checkAndMigrateTypes();
  }, [workspaceId]);

  useEffect(() => {
    const typesRef = collection(firestore, `workspaces/${workspaceId}/workstreamTypes`);
    
    const unsubscribe = onSnapshot(
      typesRef,
      (snapshot) => {
        const typesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as WorkstreamType[];
        
        setWorkstreamTypes(typesData);
        setStatus('success');
      },
      (error: FirestoreError) => {
        console.error('Workstream types error:', error);
        setStatus('error');
      }
    );

    return () => unsubscribe();
  }, [workspaceId]);

  const addWorkstreamType = async (data: Omit<WorkstreamType, 'id'>): Promise<void> => {
    const typesRef = collection(firestore, `workspaces/${workspaceId}/workstreamTypes`);
    await addDoc(typesRef, data);
  };

  const updateWorkstreamType = async (id: string, data: Partial<WorkstreamType>): Promise<void> => {
    const typeRef = doc(firestore, `workspaces/${workspaceId}/workstreamTypes/${id}`);
    await updateDoc(typeRef, data);
  };

  const deleteWorkstreamType = async (id: string): Promise<void> => {
    const typeRef = doc(firestore, `workspaces/${workspaceId}/workstreamTypes/${id}`);
    await deleteDoc(typeRef);
  };

  return {
    workstreamTypes,
    status,
    addWorkstreamType,
    updateWorkstreamType,
    deleteWorkstreamType
  };
};